import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'

import { LeftCircleIcon, RightCircleIcon } from '@/components/icons'

import { AdvertisementCardListProps } from './type'

const AUTOPLAY_INTERVAL = 4000

const useVisibleCount = () => {
  const getCount = () => {
    if (typeof window === 'undefined') return 3
    if (window.innerWidth >= 1024) return 3 // lg
    if (window.innerWidth >= 768) return 2 // md
    return 1
  }

  const [visibleCount, setVisibleCount] = useState(getCount)

  useEffect(() => {
    const handler = () => setVisibleCount(getCount())
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  return visibleCount
}

export const AdvertisementCardList = ({
  advertisements,
}: {
  advertisements: AdvertisementCardListProps
}) => {
  const [current, setCurrent] = useState(0)
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const visibleCount = useVisibleCount()

  const total = advertisements.length

  const stopAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current)
  }

  const maxIndex = Math.max(0, total - visibleCount)

  const startAutoplay = useCallback(() => {
    stopAutoplay()
    if (total <= visibleCount) return
    autoplayRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % (total - visibleCount + 1))
    }, AUTOPLAY_INTERVAL)
  }, [total, visibleCount])

  useEffect(() => {
    setCurrent((c) => Math.min(c, Math.max(0, total - visibleCount)))
  }, [visibleCount, total])

  useEffect(() => {
    startAutoplay()
    return stopAutoplay
  }, [startAutoplay])

  if (advertisements.length === 0) return null

  const canPrev = current > 0
  const canNext = current < maxIndex

  const prev = () => {
    setCurrent((c) => Math.max(0, c - 1))
    startAutoplay()
  }

  const next = () => {
    setCurrent((c) => Math.min(maxIndex, c + 1))
    startAutoplay()
  }

  const dotCount = maxIndex + 1
  const hasOverflow = total > visibleCount

  // gap is tw-gap-3 = 0.75rem
  const translateX = `calc(-${current} * ((100% + 0.75rem) / ${visibleCount}))`

  return (
    <div className="tw-relative tw-mb-5">
      {/* Slide track */}
      <div className="tw-overflow-hidden">
        <div
          className={clsx(
            'tw-flex tw-gap-3 tw-transition-transform tw-duration-300 tw-ease-in-out',
            advertisements.length < 3 ? 'tw-justify-center' : ''
          )}
          style={{ transform: `translateX(${translateX})` }}
        >
          {advertisements.map((ad) =>
            (() => {
              const url = ad.url?.trim()
              const sharedProps = {
                className: clsx(
                  'tw-flex-shrink-0 tw-rounded tw-overflow-hidden',
                  'tw-border tw-border-primary-100',
                  'tw-aspect-[387/148]',
                  url ? 'tw-cursor-pointer' : 'tw-cursor-default'
                ),
                style: {
                  width: `calc((100% - ${
                    (visibleCount - 1) * 0.75
                  }rem) / ${visibleCount})`,
                },
              }

              if (!url) {
                return (
                  <div key={ad.id} {...sharedProps} aria-label={ad.title}>
                    <img
                      src={ad.imageUrl}
                      className="tw-w-full tw-h-full tw-object-cover"
                      alt=""
                    />
                  </div>
                )
              }

              return (
                <a
                  key={ad.id}
                  {...sharedProps}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={ad.imageUrl}
                    className="tw-w-full tw-h-full tw-object-cover"
                    alt=""
                  />
                </a>
              )
            })()
          )}
        </div>
      </div>

      {/* Prev button */}
      {hasOverflow && (
        <button
          onClick={prev}
          disabled={!canPrev}
          className={clsx(
            'tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-0',
            'tw-w-7 tw-h-7 tw-rounded-full tw-border tw-border-primary-200 tw-bg-white',
            'tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-z-10',
            canPrev
              ? 'tw-opacity-100 tw-cursor-pointer'
              : 'tw-opacity-30 tw-cursor-default'
          )}
          aria-label="前へ"
        >
          <LeftCircleIcon />
        </button>
      )}

      {/* Next button */}
      {hasOverflow && (
        <button
          onClick={next}
          disabled={!canNext}
          className={clsx(
            'tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-right-0',
            'tw-w-7 tw-h-7 tw-rounded-full tw-border tw-border-primary-200 tw-bg-white',
            'tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-z-10',
            canNext
              ? 'tw-opacity-100 tw-cursor-pointer'
              : 'tw-opacity-30 tw-cursor-default'
          )}
          aria-label="次へ"
        >
          <RightCircleIcon />
        </button>
      )}

      {/* Dot indicators */}
      {hasOverflow && (
        <div className="tw-flex tw-justify-center tw-gap-1.5 tw-mt-2">
          {Array.from({ length: dotCount }).map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setCurrent(i)
                startAutoplay()
              }}
              className={clsx(
                'tw-h-1.5 tw-rounded-full tw-transition-all',
                i === current
                  ? 'tw-bg-primary-400 tw-w-3'
                  : 'tw-bg-primary-200 tw-w-1.5 hover:tw-bg-primary-300'
              )}
              aria-label={`${i + 1}ページ目`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
